import { STATIC_BASECAMP_TAB_META } from '@/lib/basecamp-tab-routing'
import {
  displayFolderTitleForStoragePath,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes'
import { BRAND } from '@/lib/config/brand'

/** Minimal tab shape for browser document.title sync. */
export type BrowserTabTitleInput = {
  id?: string
  type?: string
  title?: string | null
  explorerData?: {
    folderPath?: string | null
  }
  integrationData?: {
    projectName?: string
    listName?: string
    section?: string
  }
}

const WEAK_BASECAMP_TITLE =
  /^(basecamp|loading list\.\.\.|todo list\b|basecamp (todo|project)\b)/i

const BASECAMP_SECTION_TITLES: Record<string, string> = {
  bookmarks: 'Bookmarks',
  mystuff: 'My Stuff',
  todos: 'Todos',
  projects: 'Projects',
  activity: 'Activity',
}

function titleCaseFolderSegment(name: string): string {
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function explorerDisplayTitle(
  tab: BrowserTabTitleInput,
  workspaces: WorkspaceRouteInfo[],
): string | null {
  const folderPath = tab.explorerData?.folderPath?.trim()
  if (folderPath) {
    if (workspaces.length > 0) {
      const label = displayFolderTitleForStoragePath(folderPath, workspaces)
      if (label && !/^explorer$/i.test(label)) return label
    }
    const parts = folderPath.split('/').filter(Boolean)
    const last = parts[parts.length - 1]
    if (last && !/^ws$/i.test(last) && !/^[a-f0-9]{32}$/i.test(last)) {
      return titleCaseFolderSegment(last)
    }
  }

  const title = tab.title?.trim()
  if (title && !/^explorer$/i.test(title)) return title
  return null
}

function basecampDisplayTitle(tab: BrowserTabTitleInput): string | null {
  const title = tab.title?.trim() || ''
  const projectName = tab.integrationData?.projectName?.trim()
  const listName = tab.integrationData?.listName?.trim()
  const section = tab.integrationData?.section?.trim()

  if (tab.type === 'integration-basecamp-todo') {
    if (title && !WEAK_BASECAMP_TITLE.test(title)) return title
    return title || null
  }

  if (tab.type === 'integration-basecamp-todo-list') {
    if (listName) return listName
    if (title && !WEAK_BASECAMP_TITLE.test(title) && !/^loading/i.test(title)) {
      return title
    }
    if (projectName) return projectName
    return title || null
  }

  if (tab.type === 'integration-basecamp-triage') {
    return title || 'Weekly Triage'
  }

  // Hub / project / section
  if (projectName && (!title || WEAK_BASECAMP_TITLE.test(title))) {
    return projectName
  }
  if (title && !WEAK_BASECAMP_TITLE.test(title)) return title
  if (section && BASECAMP_SECTION_TITLES[section]) {
    return BASECAMP_SECTION_TITLES[section]
  }
  if (tab.id && STATIC_BASECAMP_TAB_META[tab.id]?.title) {
    return STATIC_BASECAMP_TAB_META[tab.id].title
  }
  return title || projectName || 'Basecamp'
}

function resolveActiveTabLabel(
  activeTab: BrowserTabTitleInput,
  workspaces: WorkspaceRouteInfo[],
): string | null {
  if (activeTab.type === 'explorer') {
    return explorerDisplayTitle(activeTab, workspaces)
  }

  if (
    activeTab.type === 'integration-basecamp' ||
    activeTab.type === 'integration-basecamp-todo' ||
    activeTab.type === 'integration-basecamp-todo-list' ||
    activeTab.type === 'integration-basecamp-triage'
  ) {
    return basecampDisplayTitle(activeTab)
  }

  const title = activeTab.title?.trim()
  return title || null
}

/**
 * Browser/OS tab title for the active in-app tab.
 * Home / empty / generic Explorer shell (no folder path) -> brand only.
 * Otherwise "{title} - Maater".
 *
 * Explorer uses folderPath (same source as in-app tab chrome), not the
 * placeholder tab.title "Explorer".
 */
export function browserTabTitleFromActiveTab(
  activeTab: BrowserTabTitleInput | null | undefined,
  brandName: string = BRAND.displayName,
  workspaces: WorkspaceRouteInfo[] = [],
): string {
  if (!activeTab) return brandName
  if (activeTab.id === 'home' || activeTab.type === 'home') return brandName

  const label = resolveActiveTabLabel(activeTab, workspaces)
  if (!label) return brandName

  return `${label} - ${brandName}`
}
